import { Col, Icon, MediaModal, Text } from 'components';
import { isToday } from 'date-fns';
import { formatLocale, theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Arrows } from 'screens/Website/Home/components';
import styled from 'styled-components';

const Container = styled.div`
  width: 100%;
  display: flex;
  background-color: ${theme.SECONDARY};
  height: 950px;
  flex-direction: column;
  position: relative;
  margin-top: 82px;

  @media (max-width: 1024px) {
    height: unset;
    margin-top: 3px;
  }
`;

const ContentContainer = styled.div`
  width: 1140px;
  margin-top: -628px;
  align-self: center;
  display: flex;
  position: relative;

  @media (max-width: 1024px) {
    width: 100%;
    margin-top: -677px;
    flex-direction: column;
  }
`;

const FeaturedHighlightImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: url(${(props) => props.src});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  width: 770px;
  height: 433px;

  @media (max-width: 1024px) {
    width: 100%;
    height: 234px;
  }
`;

const FeaturedHighlight = styled.div`
  margin: -28px 37px 0 40px;

  h4 {
    font-weight: 400;
    font-size: 24px;
    color: ${theme.SECONDARY};
  }

  p {
    font-size: 16px;
    color: #fff;
  }

  .featured-content {
    margin-top: 16px;
  }

  @media (max-width: 1024px) {
    margin: unset;

    h4 {
      margin: 16px 0 0 16px;
      font-size: 20px;
    }

    p {
      margin: 7px 0 18px 16px;
    }
  }
`;

const YouMightAlso = styled.h3`
  font-size: 24px;
  color: ${theme.SECONDARY};
  font-weight: 400;
  margin: 22px 0 -16px;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const OtherHightlightContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    flex-direction: row;
    overflow-x: scroll;
    padding-left: 16px;
  }
`;

const OtherHightlightImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: url(${(props) => props.src});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  width: 252px;
  height: 142px;
`;

const OtherHightlight = styled.div`
  margin: 35px 0 0;

  h4 {
    font-weight: 400;
    font-size: 16px;
    color: ${theme.SECONDARY};
  }

  p {
    font-size: 16px;
    color: #fff;
  }

  .other-content {
    margin-top: 14px;
  }

  @media (max-width: 1024px) {
    margin-right: 33px;

    p {
      margin-top: 5px;
    }
  }
`;

const ScrollContainer = styled.div`
  ${navigator.platform !== 'Win32' ? 'overflow-x: scroll;' : 'overflow-x: hidden;'}
  padding-left: ${(props) => props.extraPad};
  display: flex;
  margin-top: 76px;
  position: relative;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    overflow-x: scroll;
    margin: 72px 0 13px 0;
    padding-left: 17px;
  }
`;

const ArrowsContainer = styled.div`
  position: absolute;
  right: 0;
  bottom: -58px;
`;

const AthleteItem = styled.div`
  width: 330px;
  margin-right: 40px;

  .fallback-image {
    width: 330px;
    height: 330px;
    background: url(${require('assets/branding/logo-icon-grey-10.png')});
    background-size: contain;
    background-repeat: no-repeat;

    @media (max-width: 1024px) {
      width: 240px;
      height: 240px;
    }
  }

  h5  {
    font-weight: 400;
    color: #fff;
    font-size: 20px;
  }

  p  {
    font-size: 22px;
  }

  @media (max-width: 1024px) {
    width: 240px;
    margin-right: 30px;

    h5  {
      font-weight: 400;
      color: #fff;
      font-size: 16px;
    }

    p  {
      font-size: 16px;
    }
  }
`;

const BlackBg = styled.div`
  height: 628px;
  width: 1140px;
  background-color: #000;
  align-self: center;

  @media (max-width: 1024px) {
    height: 677px;
    width: 100%;
  }
`;

const serverUrl = `${process.env.REACT_APP_AWS_S3_BASE_URL}/our-athletes/`;

const ListItem = ({ t, thumbnail, video, title, onClick }) => {
  return (
    <AthleteItem>
      <MediaModal media={`${serverUrl}${video}`}>
        <div className="fallback-image">
          {!!thumbnail && (
            <Col
              style={{
                background: `url(${serverUrl}${thumbnail})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
              wid="100%"
              ht="100%"
              noFlex
              centerAll
            >
              <Icon type="playCircleWebsite" dimensions={54} col={theme.FOG} />
            </Col>
          )}
        </div>
      </MediaModal>

      <Text h5>{t('website.video')}</Text>
      <Text>{title}</Text>
    </AthleteItem>
  );
};

const Banner = (props) => {
  const { isMobile } = useSelector((state) => state.appState),
    { t } = useTranslation();

  const scrollRef = useRef(null),
    [extraPad, setExtraPad] = useState(0),
    videos = [
      {
        thumbnail: 'derrian-thumbnail.png',
        video: 'derrian-gobourne-action-video.mp4',
        title: 'p4p x Derrian Gobourne',
        uploadDate: new Date()
      },
      {
        thumbnail: 'dion-malone.png',
        video: 'dion-malone-action-video.mp4',
        title: 'p4p x Dion Malone',
        uploadDate: new Date()
      },
      {
        thumbnail: 'francis-thumbnail.png',
        video: 'francis-alexander-action-video.mp4',
        title: 'p4p x Francis Alexander',
        uploadDate: new Date()
      },
      {
        thumbnail: 'ikonka-elmont-thumbnail.png',
        video: 'ilonka-lmont-action-video.mp4',
        title: 'p4p x Ilonka Elmont',
        uploadDate: new Date()
      },
      {
        thumbnail: 'ira-brown-thumbnail.png',
        video: 'ira-brown-action-video.mp4',
        title: 'p4p x Ira Brown',
        uploadDate: new Date()
      },
      {
        thumbnail: 'micheal-clayton-thumbnail.png',
        video: 'micheal-clayton-action-video.mp4',
        title: 'p4p x Micheal Clayton',
        uploadDate: new Date()
      },
      {
        thumbnail: 'micheal-kedman-thumbnail.png',
        video: 'micheal-kedman-action-video.mp4',
        title: 'p4p x Micheal Kedman',
        uploadDate: new Date()
      },
      {
        // thumbnail: 'quicny-kameraad-thumbnail.png',
        video: 'quincy-kameraad-action-video.mp4',
        title: 'p4p x Quincy Kameraad',
        uploadDate: new Date()
      }
    ];

  useEffect(() => {
    handleExtraPad();
  }, []);

  function getDateText(date) {
    if (isToday(new Date(date))) return t('website.recently');

    return formatLocale(date, 'DD MMMM, YYYY');
  }

  function handleExtraPad() {
    setExtraPad(`${(window.innerWidth - 1140) / 2}px`);
  }

  return (
    <Container>
      <Col marg={isMobile ? '0' : '-42px 0 0'} wid="100%" noFlex>
        <BlackBg />

        <ContentContainer>
          <FeaturedHighlight>
            <MediaModal media={`${serverUrl}${videos[0].video}`}>
              <FeaturedHighlightImage src={`${serverUrl}${videos[0].thumbnail}`}>
                <Icon type="playCircleWebsite" dimensions={54} col={theme.FOG} />
              </FeaturedHighlightImage>
            </MediaModal>

            <div className="featured-content">
              <Text h4>{videos[0].title}</Text>
              <Text marg="6px 0 0">
                {t('website.uploadedDate', { dateText: getDateText(videos[0].uploadDate) })}
              </Text>
            </div>
          </FeaturedHighlight>

          <OtherHightlightContainer>
            <YouMightAlso>{t('website.youMightAlso')}</YouMightAlso>

            <OtherHightlight>
              <MediaModal media={`${serverUrl}${videos[1].video}`}>
                <OtherHightlightImage src={`${serverUrl}${videos[1].thumbnail}`}>
                  <Icon type="playCircleWebsite" dimensions={54} col={theme.FOG} />
                </OtherHightlightImage>
              </MediaModal>

              <div className="other-content">
                <Text h4>{videos[1].title}</Text>
                <Text>
                  {t('website.uploadedDate', { dateText: getDateText(videos[1].uploadDate) })}
                </Text>
              </div>
            </OtherHightlight>

            <OtherHightlight>
              <MediaModal media={`${serverUrl}${videos[2].video}`}>
                <OtherHightlightImage src={`${serverUrl}${videos[2].thumbnail}`}>
                  <Icon type="playCircleWebsite" dimensions={54} col={theme.FOG} />
                </OtherHightlightImage>
              </MediaModal>

              <div className="other-content">
                <Text h4>{videos[2].title}</Text>
                <Text>
                  {t('website.uploadedDate', { dateText: getDateText(videos[2].uploadDate) })}
                </Text>
              </div>
            </OtherHightlight>
          </OtherHightlightContainer>

          <ArrowsContainer>
            <div className="absolute-arrows">
              <Arrows col="white" elem={scrollRef} />
            </div>
          </ArrowsContainer>
        </ContentContainer>
      </Col>

      <ScrollContainer extraPad={extraPad} ref={scrollRef} classNams="bepis">
        {videos.map((x, i) => (
          <ListItem {...x} t={t} key={i} />
        ))}
      </ScrollContainer>
    </Container>
  );
};

export default Banner;
